import { HttpErrorResponse } from '@angular/common/http';
import { Component, Input, OnInit } from '@angular/core';
import { AbstractControl, FormArray, FormBuilder, FormGroup, Validators } from '@angular/forms';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { Context } from 'src/app/shared/models/context';
import { Message } from 'src/app/shared/models/domain';
import { Account, Voucher, VoucherEntry } from 'src/app/shared/models/finance';
import { AuthService } from 'src/app/shared/services/auth.service';
import { CommonService } from 'src/app/shared/services/common.service';
import { FinanceService } from 'src/app/shared/services/finance.service';

@Component({
  selector: 'app-voucher-entries',
  templateUrl: './voucher-entries.component.html',
  styleUrls: ['./voucher-entries.component.scss']
})
export class VoucherEntriesComponent implements OnInit {
  @Input() voucherEntry: VoucherEntry;
  @Input() voucher: number;
  @Input() voucherId: Voucher;
  @Input() entries: VoucherEntry[] = [];
  public voucherEntryForm: FormGroup;
  public context: Context;
  public accounts: Account[] = [];
  public submitted: boolean = false;
  private modified: boolean = false;
  public voucherEntryId!: number;
  public vouchers: VoucherEntry[] = [];
  public disabledAddButtons: boolean[] = [];


  constructor(
    private financeService: FinanceService,
    public activeModal: NgbActiveModal,
    private fb: FormBuilder,
    private commonService: CommonService,
    private authService: AuthService
  ) { }

  ngOnInit(): void {
    this.setVoucherEntry([this.voucherEntry]);
    this.context = this.authService.getContextData();
    this.financeService.getAccounts(this.context.companyId,null,false,true).subscribe(accounts => this.accounts = accounts);
  }

  private createVoucherEntry(voucherEntry?: VoucherEntry): FormGroup {
    return this.fb.group({
      id: this.fb.control(voucherEntry?.id || null),
      amount: this.fb.control(voucherEntry?.amount, [Validators.required]),
      particular: this.fb.control(voucherEntry?.particular, [Validators.required]),
      accountName: this.fb.control(voucherEntry?.accountName),
      accountId: this.fb.control(voucherEntry?.accountId, [Validators.required]),
      voucherId: this.fb.control(voucherEntry?.voucherId),
      disabled: this.fb.control(voucherEntry?.disabled, []),
      byAccount: this.fb.control(voucherEntry?.byAccount ? voucherEntry.byAccount : true),
      credit: this.fb.control(voucherEntry?.credit ? voucherEntry.credit : false),
    });
  }

  public get voucherEntries(): FormArray {
    return this.voucherEntryForm.get('voucherEntries') as FormArray;
  }

  public addVoucherEntry(voucherEntry?: VoucherEntry): void {
    const lastIndex = this.voucherEntries.length - 1;
    if (lastIndex >= 0) {
      const lastEntry = this.voucherEntries.at(lastIndex);
      lastEntry.markAllAsTouched();
      if (lastEntry.invalid) {
        this.commonService.setToastr(400, 'Please fill in the current row before adding a new one.');
        return;
      }
    }
    const newControl = this.createVoucherEntry(voucherEntry);
    this.voucherEntries.push(newControl);
    this.disabledAddButtons = this.voucherEntries.controls.map((_, idx, arr) => idx !== arr.length - 1);
  }

  private setVoucherEntry(voucherEntry: VoucherEntry[]) {
    this.voucherEntryForm = this.fb.group({
      voucherEntries: this.fb.array([]),
    });
    if (!voucherEntry || voucherEntry.length === 0) {
      this.addVoucherEntry();
    }
    voucherEntry.forEach(v => this.addVoucherEntry(v));
  }

  public savevoucherEntry(isLoading?: any) {
    this.submitted = true;
    if (this.voucherEntryForm.valid) {
      const byAccountTotal = this.voucherEntries.controls
        .filter(c => c.get('byAccount')?.value)
        .reduce((sum, c) => sum + +c.get('amount')?.value, 0);
      const toAccountTotal = this.voucherEntries.controls
        .filter(c => c.get('credit')?.value)
        .reduce((sum, c) => sum + +c.get('amount')?.value, 0);
      const hasToAccount = this.voucherEntries.controls.some(c => c.get('credit')?.value);
      if (!hasToAccount) {
        this.commonService.setToastr(400, 'At least one To Account entry is required.');
        return;
      }
      if (byAccountTotal !== toAccountTotal) {
        this.commonService.setToastr(400, `Total of By Account (${byAccountTotal}) must equal To Account (${toAccountTotal}).`);
        return;
      }
      isLoading.value = true;
      const id = this.voucherId;
      if (!id) {
        this.commonService.setToastr(400, 'Voucher ID is missing. Please save the voucher first.');
        isLoading.value = false;
        return;
      }
      const suggestionData = this.voucherEntries.value.map((item: any) => ({
        ...item,
        voucherId: id,
      }));
      this.financeService.updateVoucherEntry(suggestionData).subscribe({
        next: (response: Message) => {
          this.submitted = false;
          this.commonService.setToastr(200, response.text);
          if (!response.failed)
            this.activeModal.close(response);
          isLoading.value = false;
        },
        error: (error: HttpErrorResponse) => {
          this.commonService.setToastr(400, error?.error?.name || error.error);
          isLoading.value = false;
        }
      });
    }
  }

  public isAccountSelected(accountId: number): boolean {
    return this.entries.some(entry => entry.accountId === accountId);
  }

  public selectEntryType(index: number, type: 'byAccount' | 'credit'): void {
    const entryControl = this.voucherEntries.at(index);

    if (type === 'byAccount') {
      entryControl.get('byAccount')?.setValue(true);
      entryControl.get('credit')?.setValue(false);
    } else {
      entryControl.get('byAccount')?.setValue(false);
      entryControl.get('credit')?.setValue(true);
    }
  }

  public deleteVoucherEntry(index: number): void {
    if (this.voucherEntries.length === 1) {
      this.commonService.setToastr(400, 'At least one voucher entry is required.');
      return;
    }
    const entry = this.voucherEntries.at(index);
    if (entry.get('id')?.value) {
      this.commonService.setToastr(400, 'Cannot delete a saved entry.');
      return;
    }
    this.voucherEntries.removeAt(index);
    this.disabledAddButtons = this.voucherEntries.controls.map((_, idx, arr) => idx !== arr.length - 1);
  }
}
